import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Headline, Text } from '../../../design-system';
import { useSelector } from 'react-redux';
import { TitleWrapper, TitleContainer, HeaderDescription, PageTitle } from './Header.styles';
import { selectZipCode } from '../../reducers/zipCodeReducer';
import { zipClusterMap } from '../../constants/ListOfDealers';

type HeaderProps = {
  isNational: boolean;
  dag?: string;
  isFiltersOpen: boolean;
};

type ZipCluster = keyof typeof zipClusterMap;

const Header: React.FC<HeaderProps> = ({ isNational, dag, isFiltersOpen }) => {
  const { t } = useTranslation();
  const dagName = dag?.split('(')[0];

  const { market } = useSelector(selectZipCode);

  const isCaMarket = market === 'CA';

  const title = useMemo(() => {
    if (isCaMarket) return t('promo.header.titleCA');
    if (isNational) return t('promo.header.national');
    if (dagName && zipClusterMap[dagName as ZipCluster])
      return `${zipClusterMap[dagName as ZipCluster]} ${t('promo.header.titleUS')}`;
    return `${dagName} ${t('promo.header.titleUS')}`;
  }, [dagName, isCaMarket, isNational, t]);

  const isAlpha = !process.env.IS_LEGACY;

  const description = useMemo(() => {
    if (isCaMarket) return t('promo.header.descriptionCA');
    if (isNational) return t('promo.header.descriptionNational');
    return t(isAlpha ? 'promo.header.descriptionAudiSeason' : 'promo.header.description');
  }, [isCaMarket, isAlpha, isNational, t]);

  return (
    <TitleContainer isFiltersOpen={isFiltersOpen}>
      <TitleWrapper data-testid="header-title-wrapper">
        {!isCaMarket && !isAlpha && <PageTitle>{t('promo.header.store')}</PageTitle>}
        <Headline
          data-testid="header-title"
          tag="h1"
          variant={{
            order: '1',
            type: 'headline',
            weight: 'normal',
          }}
        >
          {title}
        </Headline>
        <HeaderDescription>
          <Text
            data-testid="header-body"
            variant={{
              order: '1',
              style: 'normal',
            }}
          >
            {description}
          </Text>
        </HeaderDescription>
      </TitleWrapper>
    </TitleContainer>
  );
};

export default Header;
